import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import './MarkdownPreview.css'

interface MarkdownPreviewProps {
    content: string
    className?: string
}

export function MarkdownPreview({ content, className = '' }: MarkdownPreviewProps) {
    return (
        <div className={`markdown-preview p-6 overflow-y-auto bg-white dark:bg-gray-900 ${className}`}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, rehypeSanitize]}
                components={{
                    // Custom renderers for better GitHub-style formatting
                    h1: ({ children }) => (
                        <h1 className="text-3xl font-bold mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                            {children}
                        </h1>
                    ),
                    h2: ({ children }) => (
                        <h2 className="text-2xl font-bold mb-3 pb-2 border-b border-gray-200 dark:border-gray-700 mt-6">
                            {children}
                        </h2>
                    ),
                    h3: ({ children }) => (
                        <h3 className="text-xl font-bold mb-2 mt-4">
                            {children}
                        </h3>
                    ),
                    h4: ({ children }) => (
                        <h4 className="text-lg font-bold mb-2 mt-3">
                            {children}
                        </h4>
                    ),
                    p: ({ children }) => (
                        <p className="mb-4 leading-7 text-gray-700 dark:text-gray-300">
                            {children}
                        </p>
                    ),
                    ul: ({ children }) => (
                        <ul className="list-disc list-inside mb-4 space-y-1 text-gray-700 dark:text-gray-300">
                            {children}
                        </ul>
                    ),
                    ol: ({ children }) => (
                        <ol className="list-decimal list-inside mb-4 space-y-1 text-gray-700 dark:text-gray-300">
                            {children}
                        </ol>
                    ),
                    li: ({ children }) => (
                        <li className="ml-4">
                            {children}
                        </li>
                    ),
                    blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 py-2 mb-4 text-gray-600 dark:text-gray-400 italic">
                            {children}
                        </blockquote>
                    ),
                    code: ({ inline, children, ...props }: React.ComponentPropsWithoutRef<'code'> & { inline?: boolean }) => {
                        return inline ? (
                            <code className="bg-gray-100 dark:bg-gray-800 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                                {children}
                            </code>
                        ) : (
                            <code className="block bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 p-4 rounded-md mb-4 overflow-x-auto font-mono text-sm" {...props}>
                                {children}
                            </code>
                        )
                    },
                    pre: ({ children }) => (
                        <pre className="mb-4">
                            {children}
                        </pre>
                    ),
                    a: ({ children, href }) => (
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                            {children}
                        </a>
                    ),
                    table: ({ children }) => (
                        <div className="overflow-x-auto mb-4">
                            <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-700">
                                {children}
                            </table>
                        </div>
                    ),
                    thead: ({ children }) => (
                        <thead className="bg-gray-100 dark:bg-gray-800">
                            {children}
                        </thead>
                    ),
                    th: ({ children }) => (
                        <th className="border border-gray-300 dark:border-gray-700 px-4 py-2 text-left font-semibold">
                            {children}
                        </th>
                    ),
                    td: ({ children }) => (
                        <td className="border border-gray-300 dark:border-gray-700 px-4 py-2">
                            {children}
                        </td>
                    ),
                    hr: () => (
                        <hr className="my-6 border-gray-300 dark:border-gray-700" />
                    ),
                    img: ({ src, alt }) => (
                        <img
                            src={src}
                            alt={alt}
                            className="max-w-full h-auto rounded-md my-4"
                        />
                    )
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    )
}
